import { useEffect } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import {
  Bold,
  Heading2,
  Italic,
  Link2,
  List,
  ListOrdered,
  Quote,
  Redo2,
  Strikethrough,
  Underline as UnderlineIcon,
  Undo2,
} from 'lucide-react';

function tightenHtml(html) {
  return String(html ?? '')
    .replace(/\u00a0/g, ' ')
    .replace(/>\s+</g, '><')
    .trim();
}

function Tb({
  pressed,
  disabled,
  title,
  onClick,
  children,
}) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      aria-pressed={pressed}
      onMouseDown={(e) => {
        /* keep editor selection when toolbar is focused */
        e.preventDefault();
      }}
      onClick={onClick}
      className={`rounded-md px-2 py-1 text-slate-600 transition dark:text-slate-300 disabled:opacity-40 ${
        pressed
          ? 'bg-brand-200 text-brand-900 dark:bg-brand-800/70 dark:text-brand-100'
          : 'hover:bg-brand-100 dark:hover:bg-brand-900/40'
      }`}
    >
      {children}
    </button>
  );
}

/**
 * @param {{ value?: string; onChange: (html: string) => void; disabled?: boolean; placeholder?: string; compact?: boolean; t?: (key: string) => string }} props
 */
export function TaskDescriptionEditor({ value = '', onChange, disabled = false, placeholder = '', compact = false, t = (key) => key }) {
  const iconSz = compact ? 13 : 15;

  const editor = useEditor({
    immediatelyRender: false,
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        bulletList: { HTMLAttributes: { class: 'list-disc pl-5 my-2' } },
        orderedList: { HTMLAttributes: { class: 'list-decimal pl-5 my-2' } },
        blockquote: { HTMLAttributes: { class: 'border-l-2 border-brand-400 pl-3 my-2 text-slate-600 dark:text-slate-300 italic' } },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        defaultProtocol: 'https',
      }),
      Placeholder.configure({
        placeholder: placeholder || t('writeDescription'),
        emptyEditorClass: 'is-editor-empty',
      }),
    ],
    editable: !disabled,
    content: value || '',
    onUpdate: ({ editor: ed }) => {
      onChange(ed.getHTML());
    },
    editorProps: {
      attributes: {
        class: `task-desc-editor-content focus:outline-none ${compact ? 'min-h-[3.75rem]' : 'min-h-[7rem]'}`,
      },
    },
  });

  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [disabled, editor]);

  useEffect(() => {
    if (!editor) return;
    const incoming = tightenHtml(value || '');
    const current = tightenHtml(editor.getHTML());
    if (incoming !== current) {
      editor.commands.setContent(value || '', false);
    }
  }, [editor, value]);

  if (!editor) {
    return (
      <div
        className={`animate-pulse rounded-xl border border-brand-200 bg-slate-50 dark:border-brand-900 dark:bg-slate-900 ${compact ? 'min-h-[5rem]' : 'min-h-[8.5rem]'}`}
      />
    );
  }

  const setLink = () => {
    const prevUrl = editor.getAttributes('link').href;
    const raw = typeof window !== 'undefined' ? window.prompt(t('editorLinkUrl'), prevUrl ?? 'https://') : null;
    if (raw === null) return;
    const trimmed = raw.trim();
    if (trimmed === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: trimmed }).run();
  };

  return (
    <div className="overflow-hidden rounded-xl border border-brand-200 bg-white dark:border-brand-900 dark:bg-slate-900">
      <div
        className={`flex flex-wrap gap-0.5 border-b border-brand-200 dark:border-brand-800 ${compact ? 'px-1 py-0.5' : 'px-2 py-1'}`}
        role="toolbar"
        aria-label={t('editorFormatting')}
      >
        <Tb disabled={disabled} title={t('editorBold')} pressed={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}>
          <Bold size={iconSz} strokeWidth={2} />
        </Tb>
        <Tb
          disabled={disabled}
          title={t('editorItalic')}
          pressed={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <Italic size={iconSz} strokeWidth={2} />
        </Tb>
        <Tb
          disabled={disabled}
          title={t('editorUnderline')}
          pressed={editor.isActive('underline')}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        >
          <UnderlineIcon size={iconSz} strokeWidth={2} />
        </Tb>
        <Tb
          disabled={disabled}
          title={t('editorStrikethrough')}
          pressed={editor.isActive('strike')}
          onClick={() => editor.chain().focus().toggleStrike().run()}
        >
          <Strikethrough size={iconSz} strokeWidth={2} />
        </Tb>
        <Tb
          disabled={disabled}
          title={t('editorHeading')}
          pressed={editor.isActive('heading', { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        >
          <Heading2 size={iconSz} strokeWidth={2} />
        </Tb>
        <Tb
          disabled={disabled}
          title={t('editorBulletedList')}
          pressed={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          <List size={iconSz} strokeWidth={2} />
        </Tb>
        <Tb
          disabled={disabled}
          title={t('editorNumberedList')}
          pressed={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <ListOrdered size={iconSz} strokeWidth={2} />
        </Tb>
        <Tb disabled={disabled} title={t('editorQuote')} pressed={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()}>
          <Quote size={iconSz} strokeWidth={2} />
        </Tb>
        <Tb disabled={disabled} title={t('editorInlineCode')} pressed={editor.isActive('code')} onClick={() => editor.chain().focus().toggleCode().run()}>
          <span className="font-mono text-xs font-semibold">{`{}`}</span>
        </Tb>
        <Tb disabled={disabled} title={editor.isActive('link') ? t('editorEditRemoveLink') : t('editorInsertLink')} pressed={editor.isActive('link')} onClick={setLink}>
          <Link2 size={iconSz} strokeWidth={2} />
        </Tb>
        <Tb disabled={disabled} title={t('editorUndo')} pressed={false} onClick={() => editor.chain().focus().undo().run()}>
          <Undo2 size={iconSz} strokeWidth={2} />
        </Tb>
        <Tb disabled={disabled} title={t('editorRedo')} pressed={false} onClick={() => editor.chain().focus().redo().run()}>
          <Redo2 size={iconSz} strokeWidth={2} />
        </Tb>
      </div>
      <div className={`task-desc-editor px-3 ${compact ? 'py-1.5' : 'py-2'}`}>
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
